// components/ModalFooterActions.jsx
import React, { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useHistory } from "react-router-dom";
import { toast } from "../../../../utils/toast";
import { useLocalization } from "../../../../context/LocalizeContextProvider";
import { useModal } from "../../../../context/ModalProvider";
import Button from "../../Button";
import PrintInvoice from "../../PrintInvoice";
import ModalEmailReceipt from "../../ModalEmailReceipt";
import { sendPaymentReceiptEmail } from "../../../../services/EmailReceiptService";
import styles from "../ModalDetailReservationV2.module.css";

const isEmailFeatureEnabled = () =>
  process.env.REACT_APP_ENABLE_EMAIL_RECEIPT === "true";

const ModalFooterActions = ({
  dataDetailReservation = {},
  detailPatient = null,
  selectedExistingPatient = null,
  dataFormNew = {},
  isNew = false,
  disableSave = false,
  disabledVisit = false,
  handleModal = () => undefined,
  handleChangeStatusReservation = () => undefined,
  handleClickSave = () => undefined,
  handleModalStartVisit = () => undefined,
  handleModalEndVisit = () => undefined,
  handleStartVisit = () => undefined,
  handleShowModalAlert = () => undefined,
  handleShowModalTransfer = () => undefined,
  // Additional flags
  isPaymentSection = false,
}) => {
  const localize = useLocalization();
  const history = useHistory();
  const modal = useModal();
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);
  const [shouldRenderPrint, setShouldRenderPrint] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const componentRef = useRef();

  // Status flags
  const isRequestMedicine =
    dataDetailReservation.status?.label === "Obat Disiapkan";
  const isWaitingMedicine =
    dataDetailReservation.status?.label === "Menunggu Obat";
  const isMedicineReady = dataDetailReservation.status?.label === "Obat Siap";
  const isActive =
    dataDetailReservation.status?.label === "Aktif" ||
    isWaitingMedicine ||
    isMedicineReady ||
    isRequestMedicine;
  const isCancel = dataDetailReservation.status?.label === "Batal";
  const isDone = dataDetailReservation.status?.label === "Selesai";
  const isReservation = dataDetailReservation.status?.label === "Reservasi";

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {
      setShouldRenderPrint(true);
      setIsLoadingPrint(!isLoadingPrint);
      const printStyle = document.createElement("style");
      printStyle.innerHTML = `
        @media print {
          @page {
            size: A4 portrait; 
          }
        }
      `;
      document.head.appendChild(printStyle);

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 500);
      });
    },
    onAfterPrint: () => {
      setIsLoadingPrint(false);
      setShouldRenderPrint(false);
      const styleTag = document.querySelector("style");
      if (styleTag) {
        document.head.removeChild(styleTag);
      }
    },
  });

  const handleStartVisitClick = () => {
    if (!!detailPatient?.activeVisitId) {
      if (dataDetailReservation?.actived_reservations?.length > 0) {
        handleShowModalAlert({
          previousReservation: dataDetailReservation?.actived_reservations[0],
          newReservation: {
            bookingId: dataDetailReservation.id,
            patientId: dataDetailReservation?.patientId,
            staffId: dataDetailReservation.doctorId,
            identificationNumber:
              detailPatient?.identificationNumber ||
              dataDetailReservation.identityNumber,
            isPatientNew: false,
            locationId: dataFormNew.locationId?.value || dataFormNew.locationId,
          },
        });
      }
    } else {
      if (
        !(
          selectedExistingPatient?.identificationNumber ||
          detailPatient?.identificationNumber ||
          dataDetailReservation.identityNumber ||
          dataFormNew.identificationNumber
        )
      ) {
        handleModalStartVisit({
          isPatientNew: isNew,
          bookingId: dataDetailReservation.id,
          paymentId: dataDetailReservation?.payment?.id || null,
          patientId: !isNew
            ? selectedExistingPatient?.id ||
              dataDetailReservation?.patientId ||
              null
            : null,
          doctorId: dataDetailReservation.doctorId,
          identificationNumber: null,
          locationId: dataFormNew.locationId?.value || dataFormNew.locationId,
        });
        return;
      }
      handleStartVisit({
        bookingId: dataDetailReservation.id,
        paymentId: dataDetailReservation?.payment?.id || null,
        patientId: !isNew
          ? selectedExistingPatient?.id ||
            dataDetailReservation?.patientId ||
            null
          : null,
        staffId: dataDetailReservation.doctorId,
        identificationNumber: !isNew
          ? selectedExistingPatient?.identificationNumber ||
            detailPatient?.identificationNumber ||
            dataDetailReservation.identityNumber
          : dataFormNew.identificationNumber,
        isPatientNew: isNew,
      });
    }
  };

  const disableEndVisit =
    !dataDetailReservation?.payment?.payment_items ||
    dataDetailReservation?.payment?.payment_items.length === 0;

  const emailFeatureEnabled = isEmailFeatureEnabled();
  const patientEmail =
    detailPatient?.email || dataDetailReservation?.patient?.email || "";
  const patientName =
    detailPatient?.name || dataDetailReservation?.patient?.name || "";

  const handleOpenEmailModal = () => setShowEmailModal(true);
  const handleCloseEmailModal = () => {
    if (isSendingEmail) return;
    setShowEmailModal(false);
  };

  const handleConfirmSendEmail = async ({ email }) => {
    if (!dataDetailReservation?.id) return;
    setIsSendingEmail(true);
    try {
      const result = await sendPaymentReceiptEmail({
        reservationId: dataDetailReservation.id,
        email,
      });
      if (result.ok) {
        toast.success(localize.getText("emailSent"));
        setShowEmailModal(false);
      } else {
        toast.error(
          `${localize.getText("emailFailed")}${
            result.error ? ` (${result.error})` : ""
          }`
        );
      }
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <>
      {/* Hidden print component */}
      {shouldRenderPrint && (
        <div style={{ display: "none" }}>
          <PrintInvoice
            ref={componentRef}
            dataDetailReservation={dataDetailReservation}
            noRm={detailPatient?.medicalRecordNumber}
            patientName={detailPatient?.name}
          />
        </div>
      )}

      {disableEndVisit && isActive && (
        <p className="m-0 text-danger text-center">
          {localize.getText("completePaymentBeforeEndVisit")}
        </p>
      )}

      <div className={styles.footer}>
        {/* Left side actions */}
        <div className={styles.footerLeftActions}>
          <Button
            label={localize.getText("close")}
            variant="outline-primary"
            className={styles.footerButton}
            onClick={() => handleModal()}
          />
        </div>

        {/* Right side actions */}
        <div className={styles.footerRightActions}>
          {/* Reservation Status Actions */}
          {isActive && (
            <Button
              label={localize.getText("movePatient")}
              variant="outline-blue"
              className={styles.footerButton}
              onClick={() => handleShowModalTransfer(dataDetailReservation?.id)}
            />
          )}

          {(isDone || isActive) && (
            <Button
              label={localize.getText("printInvoice")}
              className={styles.footerButton}
              leftIcon={<i className="far fa-file-alt" />}
              variant="warning"
              loading={isLoadingPrint}
              onClick={handlePrint}
            />
          )}

          {isDone && emailFeatureEnabled && (
            <Button
              label={localize.getText("sendEmail")}
              className={styles.footerButton}
              leftIcon={<i className="far fa-envelope" />}
              variant="warning"
              onClick={handleOpenEmailModal}
              data-testid="btn-send-email"
            />
          )}

          {isReservation && (
            <>
              <Button
                label={localize.getText("cancel")}
                variant="danger"
                className={styles.footerButton}
                onClick={() => {
                  modal.showConfirmation({
                    title: localize.getText("areYouSure"),
                    message: localize.getText("cancelledDataWarning"),
                    onConfirm: () =>
                      handleChangeStatusReservation({
                        booking_id: dataDetailReservation.id,
                        status: "CANCELLED",
                      }),
                  });
                }}
              />

              {!dataDetailReservation.patientId && (
                <Button
                  label={localize.getText("save")}
                  type={isNew ? "submit" : "button"}
                  className={styles.footerButton}
                  onClick={handleClickSave}
                  disabled={disableSave}
                />
              )}

              {!isCancel && (
                <Button
                  label={
                    dataDetailReservation.patientId
                      ? localize.getText("startVisit")
                      : localize.getText("saveAndStart")
                  }
                  disabled={disabledVisit}
                  variant="success"
                  className={styles.footerButton}
                  onClick={handleStartVisitClick}
                />
              )}
            </>
          )}

          {/* Active Status Actions */}
          {isActive && !isPaymentSection && (
            <>
              <Button
                leftIcon={<i className="fas fa-file-alt" />}
                label={localize.getText("assesment")}
                variant="success"
                className={styles.footerButton}
                onClick={() =>
                  history.push({
                    pathname: "/active-visit/assesment",
                    search: `?id=${detailPatient?.activeVisitId}&patientId=${detailPatient?.id}`,
                  })
                }
              />
              <Button
                label={localize.getText("endVisit")}
                variant="danger"
                className={styles.footerButton}
                disabled={disableEndVisit}
                onClick={() =>
                  handleModalEndVisit({
                    patientId: detailPatient?.id,
                    visitCompleted: detailPatient?.visitCompleted,
                    bookingId: dataDetailReservation.id,
                  })
                }
              />
            </>
          )}
        </div>
      </div>

      {emailFeatureEnabled && (
        <ModalEmailReceipt
          show={showEmailModal}
          patientEmail={patientEmail}
          patientName={patientName}
          isLoading={isSendingEmail}
          onCancel={handleCloseEmailModal}
          onConfirm={handleConfirmSendEmail}
        />
      )}
    </>
  );
};

export default ModalFooterActions;
